import { LOOT, SPRINT, TOWN_HALL_LEAD, MAX_LEVEL } from "./config.ts";
import { clampInt } from "./time.ts";
import type { BuildingId, SprintState, VillageState } from "./types.ts";
import { BUILDING_IDS } from "./types.ts";

const FOCUS_ORDER: BuildingId[] = [
  "townHall",
  "barracks",
  "goldMine",
  "crystalTankGoGrow",
  "crystalTankRevenue",
  "crystalTankViralz",
  "crystalTankTikwave",
  "crystalTankRihla",
  "lab",
  "workshop",
  "watchtower",
  "builderHut",
];

function capFor(id: BuildingId, townHallLevel: number): number {
  if (id === "townHall") {
    return MAX_LEVEL;
  }
  if (id === "walls") {
    return MAX_LEVEL;
  }
  return Math.min(MAX_LEVEL, townHallLevel + TOWN_HALL_LEAD);
}

export function weakestBuilding(levels: Record<BuildingId, number>): BuildingId {
  const townHallLevel = levels.townHall;
  let best: BuildingId = "townHall";
  let bestGap = -1;

  for (const id of FOCUS_ORDER) {
    const cap = capFor(id, townHallLevel);
    const gap = cap - levels[id];
    if (gap > bestGap) {
      bestGap = gap;
      best = id;
    }
  }

  return best;
}

export function startSprint(
  buildingId: BuildingId,
  atMs: number,
  durationMs: number = SPRINT.durationMs,
): SprintState {
  return {
    buildingId,
    startedAtMs: atMs,
    durationMs,
    completed: false,
  };
}

export function sprintRemainingMs(sprint: SprintState, atMs: number): number {
  if (sprint.completed) {
    return 0;
  }
  const now = sprint.pausedAtMs ?? atMs;
  const elapsed = now - sprint.startedAtMs - (sprint.pausedMs ?? 0);
  return Math.max(0, sprint.durationMs - elapsed);
}

export function sprintPaused(sprint: SprintState | null): boolean {
  return Boolean(sprint && !sprint.completed && sprint.pausedAtMs != null);
}

export function pauseSprint(state: VillageState, atMs: number): VillageState {
  const sprint = state.sprint;
  if (!sprint || sprint.completed || sprint.pausedAtMs != null) {
    return state;
  }
  return { ...state, sprint: { ...sprint, pausedAtMs: atMs } };
}

export function resumeSprint(state: VillageState, atMs: number): VillageState {
  const sprint = state.sprint;
  if (!sprint || sprint.completed || sprint.pausedAtMs == null) {
    return state;
  }
  const pausedMs = (sprint.pausedMs ?? 0) + Math.max(0, atMs - sprint.pausedAtMs);
  return { ...state, sprint: { ...sprint, pausedAtMs: null, pausedMs } };
}

export function sprintReady(sprint: SprintState, atMs: number): boolean {
  return !sprint.completed && sprint.pausedAtMs == null && sprintRemainingMs(sprint, atMs) === 0;
}

/** Levels a building can still gain before it hits the Keep cap. */
export function levelsUnderCap(id: BuildingId, levels: Record<BuildingId, number>): number {
  return Math.max(0, capFor(id, levels.townHall) - levels[id]);
}

export function completeSprint(
  state: VillageState,
  atMs: number,
): { ok: true; state: VillageState } | { ok: false; error: "no_sprint" | "not_finished" } {
  const sprint = state.sprint;
  if (!sprint) {
    return { ok: false, error: "no_sprint" };
  }
  if (!sprintReady(sprint, atMs)) {
    return { ok: false, error: "not_finished" };
  }

  const next: VillageState = {
    ...state,
    gold: clampInt(state.gold + LOOT.sprintGold, 0, LOOT.maxCarry),
    crystals: clampInt(state.crystals + LOOT.sprintCrystals, 0, LOOT.maxCarry),
    sprint: { ...sprint, completed: true },
    justLeveled: [],
  };

  return { ok: true, state: next };
}

export function cancelSprint(state: VillageState): VillageState {
  return { ...state, sprint: null };
}

export function sprintDurationMs(fast: boolean): number {
  return fast ? SPRINT.fastDurationMs : SPRINT.durationMs;
}

export { BUILDING_IDS };
